import { useQuery } from "@tanstack/react-query";
import {
  getDecideQueue,
  getPortfolio,
  type DecideQueue,
  type PortfolioStrip,
} from "@/services/api/harnessApi";

/**
 * The ONE attention store. Built to P144's own decisions:
 *
 *  - **D-144-001** — the store is react-query shared KEYS, not a new zustand store.
 *  - **D-144-008** — the badge is REUSED, never re-created (one component, one look).
 *  - **D-144-011** — the count carries QUESTIONS *and* PREPARATIONS, and the dot says which.
 *
 * WHY ONE KEY, and why that is the point rather than a detail. `POST /harness/portfolio` is
 * owner-scoped and already returns every entity the caller can read, so ONE query serves every
 * consumer — the entity switcher, the sidebar rows, the email tab. react-query de-duplicates
 * observers on the same key, so a new consumer adds a READER and never a POLLER. That is this
 * project's founding defect (IN-01: three independent pollers, three endpoints, three scopes)
 * removed *by construction* instead of by remembering. A future attention source extends THIS
 * query's data; it does not get a hook of its own.
 *
 * HONESTY (P133 D-133-003 + the badge's own rules). A failed request yields no counts, and the
 * badge renders nothing for an absent count: there is no placeholder and no optimistic decrement
 * (D-144-009 — the number moves on a recorded decision, never on a click). "0" and "request
 * failed" are both silence on screen, and this hook invents a number for neither.
 *
 * NOT PERSISTED, deliberately — see `CRM.tsx`'s `dehydrateOptions`. The app persists its query
 * cache, and a rehydrated attention count would be a number of unknown age presented as current.
 * The dot is either real and fresh, or the first fetch has not landed yet and it is absent.
 */
export const ATTENTION_QUERY_KEY = ["harness-portfolio"] as const;

export interface AttentionCount {
  /** Everything needing this entity: the questions below + the preparations. */
  total: number;
  /** Drafts written and waiting on a decision — the email surface's own work (D-144-011). */
  preparation: number;
  /** A process output awaiting approve/reject. */
  processGate: number;
  /** P144 D-144-019 — how many of `processGate` carry a recorded first view ("he opened it"). */
  processGateViewed: number;
  /** P144 D-144-019 — how many of `preparation` the reader has opened (drafts, `draft_views`). */
  preparationViewed: number;
  /** Whether the drafts' view fact could be READ at all (migration 000032; false on an
   *  environment without it). A count may never dim on facts it does not have — so this flag, not
   *  the zero, decides. */
  preparationViewFacts: boolean;
  /** A wake asking for a decision. */
  hermesWake: number;
  /** P144 D-144-017 — the wake items that ASK for a decision (the red half of that lane). */
  wakeDecisions: number;
  /** Worth knowing: real signals asking for no verdict. Visible, named, never red. */
  knowing: number;
}

export function useAttentionCounts(pollMs = 60_000): {
  counts: Record<string, AttentionCount>;
  failed: boolean;
  /** T0197 — the portfolio strip, riding the SAME query. A new consumer adds a READER and
   *  never a POLLER (this hook's founding rule), so the strip costs no extra request. */
  strip?: PortfolioStrip;
} {
  const query = useQuery({
    queryKey: ATTENTION_QUERY_KEY,
    queryFn: getPortfolio,
    refetchInterval: pollMs,
    retry: false,
  });

  // A lane the backend could not read reports 0 WITH ITS REASON in its own lane object; it is
  // never counted as work and never faked as one. Here that only means we mirror what arrived.
  const counts: Record<string, AttentionCount> = {};
  const rows = query.data?.status ? query.data.data?.entities ?? [] : [];
  for (const e of rows) {
    // A ROW THAT NAMES NO COMPANY IS NOT A ZERO (P144 F-10, 2026-09-28). The portfolio answers
    // `error: "unknown entity"` for an id that is not a company — it did not look, so there is
    // no number to report, and reporting 0 would say "nothing is waiting there". Left out of
    // `counts` so `useAttentionCount()` answers `undefined` ("we have not heard") instead of a
    // fabricated silence. `total` is deliberately NOT invented for it either.
    if (e.error) continue;
    counts[e.entity_id] = {
      total: e.total ?? 0,
      preparation: e.preparation ?? 0,
      processGate: e.process_gate ?? 0,
      processGateViewed: e.process_gate_viewed ?? 0,
      preparationViewed: e.preparation_viewed ?? 0,
      preparationViewFacts: e.preparation_view_facts === true,
      hermesWake: e.hermes_wake ?? 0,
      wakeDecisions: e.hermes_wake_decisions ?? 0,
      knowing: e.knowing ?? 0,
    };
  }

  return { counts, failed: query.isError, strip: query.data?.status ? query.data.data?.strip : undefined };
}

/**
 * The count for ONE entity, or `undefined` while it is unknown.
 *
 * `undefined` and `0` are different answers and the badge treats them differently-adjacent: both
 * render nothing, but a caller that needs to distinguish "we have not heard" from "nothing is
 * waiting" can, which is what keeps an honest zero from being confused with a failed read.
 */
export function useAttentionCount(entityId?: string | null): AttentionCount | undefined {
  const { counts } = useAttentionCounts();
  return entityId ? counts[entityId] : undefined;
}

/**
 * The SUM across every entity the caller can read — IN-04's top-down number.
 *
 * "it should be visible we are always thinking top down". It is derived from the same rows the
 * per-entity badges use, so it can never disagree with them; a sum computed from a second read
 * is the very defect this hook exists to remove.
 */
export function useAttentionTotal(): number {
  const { counts } = useAttentionCounts();
  return Object.values(counts).reduce((n, c) => n + (c.total ?? 0), 0);
}

/**
 * IS THIS NUMBER IN ITS QUIET STATE? — P144 D-144-019 (2026-09-28).
 *
 * The PO: *"i rather would do fade to colour intensity to 50%, not fade completely away"*. The
 * number goes to half intensity when **every item it counts has been opened** — and it NEVER goes
 * away, and it never changes value: `D-144-009` keeps the dot clearing on a DECISION.
 *
 * THE `countedByViewFacts` ARGUMENT IS THE WHOLE POINT, not a detail: a total mixes lanes, and only
 * the lanes that record a view may vouch for one. A company whose 133 items are all *drafts* would
 * otherwise read "opened 0 of 0" and dim a number whose every item has never been opened — a
 * fabricated "you have looked". So a number may only go quiet when the part that has view facts IS
 * the whole number AND every one of those was opened. Since 2026-09-28 TWO lanes record views —
 * process reviews (`review_views`, project146 T0192) and drafts (`draft_views`, migration 000032) —
 * and each caller passes its own lane's facts; the guard itself is unchanged, because the rule was
 * already the right one.
 *
 *   0/3 opened          → full red   (there is something here you have not opened)
 *   3/3 opened          → 50%        (you have looked; the decision is still yours)
 *   3/3 opened of 136   → full red   (133 drafts are in this number and none was ever opened)
 *   no view facts       → full red   (no lane here can say "you looked")
 */
export function isQuiet(
  count?: number | null,
  opened?: number | null,
  countedByViewFacts?: number | null,
): boolean {
  const n = count ?? 0;
  const o = opened ?? 0;
  const withFacts = countedByViewFacts ?? 0;
  return n > 0 && withFacts === n && o >= n;
}

/**
 * The per-entity ITEMS behind the number — the decide queue, in the same store as the counts.
 *
 * It lives here rather than in the surface that renders it because a page that polls for itself
 * is the founding defect (IN-01). Two surfaces already read this key by convention
 * (`HarnessHomePage`, the review surface); naming the key here makes the third consumer a reader
 * and never a third fetch. Same 30 s cadence the audit surface used, per the plan's one-store
 * design (D-144-001: the decide-queue already lived in react-query).
 */
export const decideQueueQueryKey = (entityId?: string | null) =>
  ["harness-decide-queue", entityId] as const;

export function useDecideQueue(
  entityId?: string | null,
  options: { enabled?: boolean; pollMs?: number } = {},
) {
  const { enabled = true, pollMs = 30_000 } = options;
  return useQuery({
    queryKey: decideQueueQueryKey(entityId),
    queryFn: async () => {
      if (!entityId) return null;
      const res = await getDecideQueue(entityId, 30);
      if (!res?.status) throw new Error(res?.message || "Entity review queue failed");
      return (res.data ?? null) as DecideQueue | null;
    },
    enabled: Boolean(entityId) && enabled,
    refetchInterval: pollMs,
    staleTime: 10_000,
  });
}
